import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioButtonGroup,
  Textarea,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { createPermission } from '../../services/api';

type Props = {
  onCreated: () => void;
};

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_:]/g, '');
}

function isSlugValid(slug: string): boolean {
  return slug.length > 0 && /^[a-z0-9][a-z0-9\-_:]*$/.test(slug);
}

const INTERNAL_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

const CreatePermissionButton: React.FC<Props> = ({ onCreated }) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [isInternal, setIsInternal] = useState('no');
  const [isCreating, setIsCreating] = useState(false);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(nameToSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value);
  };

  const resetForm = () => {
    setName('');
    setSlug('');
    setSlugManuallyEdited(false);
    setDescription('');
    setIsInternal('no');
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const slugError =
    slug && !isSlugValid(slug)
      ? 'Slug must be lowercase, start with a letter or number, and only contain letters, numbers, hyphens, underscores, and colons'
      : undefined;

  const isFormValid =
    name.trim() !== '' &&
    slug.trim() !== '' &&
    isSlugValid(slug) &&
    description.trim() !== '';

  const handleCreate = async () => {
    if (!isFormValid) return;
    setIsCreating(true);
    try {
      await createPermission({
        body: {
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim(),
          is_internal: isInternal === 'yes',
        },
      });
      showSuccess(
        'Permission Created',
        `Permission "${name.trim()}" was created successfully.`
      );
      onCreated();
      handleClose();
    } catch (error) {
      console.error('Failed to create permission:', error);
      showError(
        'Failed to Create Permission',
        'Unable to create permission. Please try again.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Button variant='purple' size='sm' onClick={() => setOpen(true)}>
        <Plus className='h-4 w-4' />
        New Permission
      </Button>

      <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Create New Permission</DialogTitle>
          </DialogHeader>

          <div className='space-y-5 py-2'>
            {/* Name */}
            <div className='space-y-1.5'>
              <Label htmlFor='perm-name'>
                Name <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='perm-name'
                placeholder='e.g., Read Patients'
                value={name}
                onChange={e => handleNameChange(e.target.value)}
              />
            </div>

            {/* Slug */}
            <div className='space-y-1.5'>
              <Label htmlFor='perm-slug'>
                Slug <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='perm-slug'
                placeholder='e.g., patient:read'
                value={slug}
                onChange={e => handleSlugChange(e.target.value)}
                className={
                  slugError ? 'border-red-500 focus-visible:ring-red-500' : ''
                }
              />
              {slugError && <p className='text-xs text-red-500'>{slugError}</p>}
            </div>

            {/* Description */}
            <div className='space-y-1.5'>
              <Label htmlFor='perm-description'>
                Description <span className='text-red-500'>*</span>
              </Label>
              <Textarea
                id='perm-description'
                placeholder='Describe what this permission grants...'
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Admin only */}
            <div className='space-y-1.5'>
              <Label>
                Admin only <span className='text-red-500'>*</span>
              </Label>
              <p className='text-xs text-muted-foreground'>
                When yes, only admin users can see or assign this permission.
              </p>
              <RadioButtonGroup
                orientation='horizontal'
                value={isInternal}
                onValueChange={setIsInternal}
                options={INTERNAL_OPTIONS}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant='purple'
              onClick={handleCreate}
              disabled={!isFormValid || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatePermissionButton;
